import {
  Dispatch,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import debounce from 'lodash/debounce';
import { useNavigate, NavigateOptions } from 'react-router-dom';
import { isInUIC } from './common';
import config from 'src/config';
interface Action<I> {
  type: string;
  payload: Partial<I> | any;
}
// function useEnhancedReducerI<I>(
//   reducer: (state: I, action: Action<I>) => I,
//   initState: I,
//   initializer?: (init: I) => I
// ): [I, Dispatch<Action<I>>, Function];
export function useEnhancedReducer<T>(
  reducer: (state: T, action: Action<T>) => T,
  initState: T,
  initializer?: (arg: T) => T
): [T, Dispatch<Action<T>>, Function] {
  const latestState = useRef<ReturnType<typeof reducer>>(initState);
  const getState = useCallback(() => latestState.current, []);
  return [
    ...useReducer(
      (state: T, action: Action<T>) =>
        (latestState.current = reducer(state, action)),
      initState,
      initializer as undefined
    ),
    getState,
  ];
}

// export const useEnhancedReducer = (
//   reducer: Parameters<typeof useReducer>[0],
//   initState: Parameters<typeof useReducer>[1],
//   initializer?: Parameters<typeof useReducer>[2]
// ): [typeof initState, Dispatch<Action<typeof initState>>, Function] => {
//   const latestState = useRef<ReturnType<typeof reducer>>(initState);
//   const getState = useCallback(() => latestState.current, []);
//   return [
//     ...useReducer(
//       (state: typeof initState, action: Action<typeof initState>) =>
//         (latestState.current = reducer(state, action)),
//       initState,
//       initializer
//     ),
//     getState,
//   ];
// };

export function useWhyDidYouUpdate(name: string, props: any) {
  // Get a mutable ref object where we can store props ...
  // ... for comparison next time this hook runs.
  const previousProps: any = useRef();

  useEffect(() => {
    if (previousProps.current) {
      // Get all keys from previous and current props
      const allKeys = Object.keys({ ...previousProps.current, ...props });
      // Use this object to keep track of changed props
      const changesObj: any = {};
      // Iterate through keys
      allKeys.forEach((key) => {
        // If previous is different from current
        if (previousProps.current[key] !== props[key]) {
          // Add to changesObj
          changesObj[key] = {
            from: previousProps.current[key],
            to: props[key],
          };
        }
      });

      // If changesObj not empty then output to console
      if (Object.keys(changesObj).length) {
        // eslint-disable-next-line no-console
        console.log('[why-did-you-update]', name, changesObj);
      }
    }

    // Finally update previousProps with current props for next hook call
    previousProps.current = props;
  });
}

export const useDebounceState = <T>(initValue: T, delay = 200) => {
  const [value, setValue] = useState(initValue);
  const debounceSetValue = useMemo(() => {
    return debounce(setValue, delay);
  }, [delay]);

  return [value, debounceSetValue] as const;
};

/**
 * Custom hook that wraps useNavigate. Always drives the iframe's React Router
 * via baseNavigate, and additionally pushes to Vision One's shell router in
 * UIC mode so iframe.src and the parent address bar align with the navigation.
 *
 * Defense in depth: if the shell router push is a no-op (e.g. target URL
 * matches the current iframe.src) or throws (cross-origin SecurityError, APP
 * absent during shell init), baseNavigate has already moved the user.
 *
 * @example
 * const navigate = useNavigateWithHandler();
 * navigate('/some-path');
 */
export const useNavigateWithHandler = () => {
  const baseNavigate = useNavigate();

  return useCallback(
    (path: string | number, options?: NavigateOptions) => {
      // Numeric back/forward: shell router doesn't accept numbers,
      // and React Router's navigate(number, options) isn't a valid signature.
      if (typeof path === 'number') {
        baseNavigate(path);
        return;
      }

      // Always drive the iframe's React Router first.
      baseNavigate(path, options);

      if (!isInUIC()) return;

      const href = path.replace(/^\/+/, '');
      const prefix = config.ui.uicPrefix.replace(/\/+$/, '');
      const uicPath = `${prefix}/${href}`;

      try {
        // NOTE: reading window.parent.APP throws SecurityError cross-origin.
        // Do NOT hoist this read out of the try block — optional chaining
        // does not catch SecurityError.
        const shellRouter = (window.parent as any)?.APP?.$router;
        if (!shellRouter) return;
        if (options?.replace) {
          shellRouter.replace(uicPath);
        } else {
          shellRouter.push(uicPath);
        }
      } catch {
        // cross-origin SecurityError or APP absent — baseNavigate already ran
      }
    },
    [baseNavigate]
  );
};
